/**
 * track — minimal client-side analytics for /jub funnel measurement.
 *
 * Until now, /jub had ZERO observability:
 *   • Likes/Saves persisted to localStorage only
 *   • Tier-funnel banner clicks vanished
 *   • Tier upgrade-card CTA hits jubit.ai/billing without breadcrumb
 *
 * This is the smallest viable infra: a pure helper that POSTs an event
 * to `/api/events` (Vercel function — see `apps/web/api/events.ts`).
 * Server-side just `console.log`s the JSON; Vercel log retention captures
 * it for queries via the dashboard. No npm package, no DB migration —
 * we can iterate to a real events table later without touching the
 * client API.
 *
 * Reliability:
 *   • Uses `navigator.sendBeacon` when available (survives page nav,
 *     fire-and-forget). Falls back to `fetch({ keepalive: true })`.
 *   • No-op SSR-safe (typeof window guard).
 *   • Silent on network failure — analytics never blocks the user
 *     action it's tracking.
 *
 * Privacy:
 *   • Client only sends what the call site provides — pass IDs +
 *     tier, NEVER prompts/PII.
 *   • Server-side `userId` is filled from the Supabase session cookie
 *     (when present) so client doesn't need to send it.
 */

/**
 * Event-type union. Keep small + explicit so consumers don't fan out
 * inconsistently. Each new event added here gets:
 *   1. A type-narrowed properties shape below (TrackEventProps)
 *   2. A grep-friendly call site documented in the JSDoc
 *
 * Naming: `<surface>_<action>` (snake_case). Past tense for state
 * transitions ('signed_in'), present for intent clicks ('clicked').
 */
export type TrackEventType =
  | 'funnel_signin_clicked'      // TierFunnelBanner Sign-in CTA
  | 'funnel_signin_dismissed'    // TierFunnelBanner ✕ click
  | 'tier_upgrade_clicked'       // TierUpgradeCard "Upgrade to X →"
  | 'gallery_like_toggled'       // GalleryCard heart
  | 'gallery_save_toggled'       // GalleryCard bookmark
  | 'detail_opened'              // DetailScreen mount via card click
  | 'image_publish_toggled'      // ProfileScreen visibility flip
  | 'nsfw_card_tapped'           // GalleryCard blurred-card tap
  | 'nsfw_upsell_shown'          // NsfwUpsellCard mount impression (ADR-0135)
  | 'nsfw_upsell_clicked'        // NsfwUpsellCard CTA click (ADR-0135)
  | 'age_gate_confirmed'         // AgeGateModal Confirm 18+
  | 'age_gate_cancelled'         // AgeGateModal Cancel / dismiss
  | 'credit_refill_clicked'      // ProfileScreen "Refill ju" CTA — pre-Stripe placeholder
                                 // intent capture (ADR-0108 cross-repo Stripe arc).
  | 'credit_refill_returned'      // Stripe return toast on this origin (P7-053 R-6)
  // ─── ADR-0119 Studio Wizard (PR M.1) ──────────────────────────────
  | 'studio_wizard_entered'      // <StudioWizard> mount + ?wizard=1 toggle ON
  | 'studio_wizard_exited'       // toggle OFF / "Skip to Classic" click
  | 'studio_phase_entered'       // Phase ① / ② / ③ activated
  | 'studio_phase_completed'     // user clicks "Continue → Phase N+1"
  | 'studio_axis_picked'         // chip pick within a phase (axis = camera|outfit|...)
  | 'studio_classic_fallback'    // user bails from wizard mid-build
  | 'studio_generate_clicked'    // Generate fires from wizard mode
  // ─── /planet retention (ADR-0309) — anonymous, opt-out; sent ONLY through
  //     planet/telemetry/planetTelemetry.ts, which owns consent + identity ───
  | 'planet_session_start'       // route mount {world, district, mode}
  | 'planet_session_heartbeat'   // every 60 s while mounted
  | 'planet_session_end'         // pagehide {durationMs} (beacon)
  | 'planet_first_input'         // first key / pointer {ms since mount}
  | 'planet_mode_start'          // a mode became active {mode}
  | 'planet_run_finish'          // {mode, outcome, durationMs, scoreBucket}
  | 'planet_mode_end'            // a mode stopped being active {mode, district, durationMs} —
                                 // closes every mode_start, so endless modes (park) get a span too
  | 'planet_moment_shared'       // a moment card left the page {mode, outcome} — never its text
  | 'planet_sea_errand_pick'     // a suggested action was picked {mode, outcome: lane} — never its text
  | 'planet_buddy_bark_shown'    // an authored buddy line was shown {buddy, barkEvent} — never its text
  | 'planet_buddy_muted'        // the player silenced buddy barks {buddy} — never a transcript
  | 'planet_park_sample'       // one park action: research id + kerb packet + play outcome
  // ─── Hero companion slots (ADR-0378 / SPEC-ju-companion-slots-P2) ───
  | 'companion_generate_paid'    // forge generate ju landed (Forge P2)
  | 'companion_wire_paid'        // hero slot wire ju landed
  | 'companion_wire_first_party' // SAMPLE glb:* wired free

/** Standardized event props. Free-form `extra` for future fields. */
export interface TrackEventProps {
  /** Free | Pro | Max | anonymous — whoever's signed in. */
  readonly tier?: 'anonymous' | 'free' | 'pro' | 'max';
  /** Surface where the event fired (e.g. 'gallery', 'community', 'profile'). */
  readonly surface?: string;
  /** Optional resource id (image id, library brick id, etc.). */
  readonly resourceId?: string | number;
  /** Optional resource domain (theme/persona/creature/...). */
  readonly resourceDomain?: string;
  /** Optional outbound destination URL (for upgrade clicks). */
  readonly destination?: string;
  /** /planet telemetry identity (ADR-0309): a random, resettable install id —
   *  never an account id — plus a per-page-load session id and the platform. */
  readonly installId?: string;
  readonly sessionId?: string;
  readonly platform?: 'web' | 'steam';
  /** Catch-all for one-off props. Keep small + serializable. */
  readonly extra?: Readonly<Record<string, string | number | boolean | null>>;
}

/**
 * Pure-fn — build the request body. Exported for unit testing.
 * Adds a `ts` (ISO string) at send time + a small `path` hint so
 * server-side logs stay self-describing without an extra DB join.
 */
export function buildEventPayload(
  type: TrackEventType,
  props: TrackEventProps,
): Readonly<Record<string, unknown>> {
  return {
    type,
    ts: new Date().toISOString(),
    path: typeof window !== 'undefined' ? window.location.pathname + window.location.search : null,
    ...props,
  };
}

/**
 * Fire-and-forget event tracker. Returns void — never throws,
 * never awaits. Calling it from a click handler doesn't slow the
 * user-visible action.
 *
 * @example
 *   <button onClick={() => {
 *     trackEvent('tier_upgrade_clicked', { tier: 'free', surface: 'profile' });
 *     window.location.href = 'https://jubit.ai/billing';
 *   }}>Upgrade</button>
 */
export function trackEvent(type: TrackEventType, props: TrackEventProps = {}): void {
  if (typeof window === 'undefined') return;
  const body = buildEventPayload(type, props);
  const payload = JSON.stringify(body);
  // sendBeacon is the right primitive for fire-and-forget telemetry —
  // it survives page-unload (e.g. user clicks an outbound link).
  // Most browsers cap it at 64KB; our payloads are well under.
  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    try {
      // Blob constructor takes the JSON + content-type; some servers
      // reject `application/json` POSTs from sendBeacon (CORS quirk),
      // so we use 'text/plain' which the /api/events fn parses anyway.
      const blob = new Blob([payload], { type: 'text/plain;charset=UTF-8' });
      navigator.sendBeacon('/api/events', blob);
      return;
    } catch {
      // Fall through to fetch fallback.
    }
  }
  // Fallback for environments without sendBeacon (mostly old Safari).
  // `keepalive: true` lets the request survive a navigation.
  try {
    void fetch('/api/events', {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: payload,
    });
  } catch {
    // Silent — analytics never blocks the user-visible action.
  }
}
